import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import ProductApi from "../../services/ProductApi";
import FamilyApi from "../../services/FamilyApi";
import { useToast } from "../../context/ToastContext";
import { classNames } from "primereact/utils";

// Design System
import FormCard from "../../components/FormCard";
import Input from "../../components/Input/Input";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";

interface ProductOption {
  label: string;
  value: string;
}

const EditFormProduct: React.FC = () => {
  const { t: translateForm } = useTranslation("form");
  const { t: translateMessages } = useTranslation("Messages");
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { displayToast } = useToast();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [partNumber, setPartNumber] = useState("");
  const [isProducible, setIsProducible] = useState<boolean>(true);
  const [familyId, setFamilyId] = useState<string>("");
  const [families, setFamilies] = useState<ProductOption[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  const nameRef = useRef<any>(null);
  const descriptionRef = useRef<any>(null);
  const partNumberRef = useRef<any>(null);
  const statusRef = useRef<any>(null);
  const familyRef = useRef<any>(null);

  useEffect(() => {
    FamilyApi.GetAllFamilies()
      .then((res) => {
        const opts = res.map((f: any) => ({ label: f.name, value: f.id }));
        setFamilies(opts);
      })
      .catch(() => {
        displayToast("error", translateMessages("error"), translateForm("errorLoadingFamilies"), false, 3000);
      });

    if (id) {
      ProductApi.GetById(id)
        .then((response) => {
          const item = response;
          setName(item.name || "");
          setDescription(item.description || "");
          setPartNumber(item.partNumber || "");
          setIsProducible(item.isProducible ?? true);
          setFamilyId(item.familyId || "");
        })
        .catch(() => {
          displayToast("error", translateMessages("error"), translateForm("errorLoadingProduct"), false, 3000);
        });
    }
  }, [id, displayToast, translateMessages, translateForm]);

  const validate = (): boolean => {
    const newErr: Record<string, string> = {};

    if (!name.trim()) newErr.name = translateForm("nameRequired");
    else if (name.length > 50) newErr.name = translateForm("NameExceptionCharacters");

    if (!description.trim()) newErr.description = translateForm("DescriptionRequired");
    else if (description.length > 600) newErr.description = translateForm("DescriptionExceptionCharacters");

    if (!partNumber.trim()) newErr.partNumber = translateForm("PartNumberRequired");
    else if (!/^[a-zA-Z0-9]+$/.test(partNumber)) newErr.partNumber = translateForm("PartNumberAlphanumeric");
    else if (partNumber.length > 15) newErr.partNumber = translateForm("PartNumberMaxLength");

    if (familyId === "") newErr.familyId = translateForm("FamilyRequired");

    setErrors(newErr);
    return Object.values(newErr).every((e) => !e);
  };

  const handleSave = async (e?: React.FormEvent): Promise<void> => {
    e?.preventDefault();
    if (!validate()) {
        if (!familyId) familyRef.current?.focus();
        else if (!partNumber.trim()) partNumberRef.current?.focus();
        else if (!name.trim()) nameRef.current?.focus();
        else if (!description.trim()) descriptionRef.current?.focus();
        return;
    }

    if (!id) {
      displayToast("error", translateMessages("error"), translateForm("errorUpdatingProduct"), false, 3000);
      return;
    }

    const payload = {
      id,
      name,
      description,
      partNumber,
      isProducible,
      familyId,
    };

    try {
      if (id) await ProductApi.UpdateProduct(payload);
      displayToast("success", translateForm("Success"), translateForm("ProductUpdated"), false, 3000);
      navigate("/products");
    } catch (error: any) {
      const msg = error?.data?.data || translateForm("errorUpdatingProduct");
      displayToast("error", translateForm("errorUpdatingProduct"), msg, false, 3000);
    }
  };

  const handleCancel = (): void => {
    setShowConfirmCancel(true);
  };

  return (
    <FormCard
      title={translateForm("editProduct")}
      icon="edit"
      submitButtonLabel={translateForm("save")}
      cancelButtonLabel={translateForm("Cancel")}
      onSubmit={handleSave}
      onCancel={handleCancel}
      numColumns={12}
    >
      <div style={{ gridColumn: "span 6" }}>
        <Input
          ref={familyRef}
          type="select"
          label={translateForm("Family")}
          value={familyId}
          options={families}
          optionLabel="label"
          optionValue="value"
          required
          placeholder={`${translateForm("Family")} *`}
          className={classNames("w-full", { "p-invalid border-red": errors.familyId })}
          onChange={(val: any) => {
            setFamilyId(val);
            setErrors((prev) => ({ ...prev, familyId: "" }));
          }}
        />
        {errors.familyId && (
          <small className="p-error" style={{ color: "var(--color-error-500)", fontSize: "0.75rem", marginTop: "4px", display: "block" }}>{errors.familyId}</small>
        )}
      </div>

      <div style={{ gridColumn: "span 6" }}>
        <Input
          ref={statusRef}
          type="select"
          label={translateForm("status")}
          value={isProducible}
          options={[
            { label: translateForm("active") || "Ativo", value: true },
            { label: translateForm("inactive") || "Inativo", value: false },
          ]}
          optionLabel="label"
          optionValue="value"
          required
          placeholder={`${translateForm("status")} *`}
          className={classNames("w-full", { "p-invalid border-red": errors.isProducible })}
          onChange={(val: any) => {
            setIsProducible(val);
            setErrors((prev) => ({ ...prev, isProducible: "" }));
          }}
        />
        {errors.isProducible && (
          <small className="p-error" style={{ color: "var(--color-error-500)", fontSize: "0.75rem", marginTop: "4px", display: "block" }}>{errors.isProducible}</small>
        )}
      </div>

      <div style={{ gridColumn: "span 6" }}>
        <Input
          ref={partNumberRef}
          type="text"
          label={translateForm("partNumber")}
          value={partNumber}
          required
          placeholder={`${translateForm("partNumber")} *`}
          className={classNames("w-full", { "p-invalid border-red": errors.partNumber })}
          onChange={(val: any) => {
            setPartNumber(val);
            setErrors((prev) => ({ ...prev, partNumber: "" }));
          }}
        />
        {errors.partNumber && (
          <small className="p-error" style={{ color: "var(--color-error-500)", fontSize: "0.75rem", marginTop: "4px", display: "block" }}>{errors.partNumber}</small>
        )}
      </div>

      <div style={{ gridColumn: "span 6" }}>
        <Input
          ref={nameRef}
          type="text"
          label={translateForm("Name")}
          value={name}
          required
          placeholder={`${translateForm("Name")} *`}
          className={classNames("w-full", { "p-invalid border-red": errors.name })}
          onChange={(val: any) => {
            setName(val);
            setErrors((prev) => ({ ...prev, name: "" }));
          }}
        />
        {errors.name && (
          <small className="p-error" style={{ color: "var(--color-error-500)", fontSize: "0.75rem", marginTop: "4px", display: "block" }}>{errors.name}</small>
        )}
      </div>

      <div style={{ gridColumn: "span 12" }}>
        <Input
          ref={descriptionRef}
          type="textArea"
          label={translateForm("description")}
          value={description}
          required
          rows={3}
          placeholder={`${translateForm("description")} *`}
          className={classNames("w-full", { "p-invalid border-red": errors.description })}
          onChange={(val: any) => {
            setDescription(val);
            setErrors((prev) => ({ ...prev, description: "" }));
          }}
        />
        {errors.description && (
          <small className="p-error" style={{ color: "var(--color-error-500)", fontSize: "0.75rem", marginTop: "4px", display: "block" }}>{errors.description}</small>
        )}
      </div>

      <ConfirmModal
        visible={showConfirmCancel}
        onHide={() => setShowConfirmCancel(false)}
        onConfirm={() => navigate("/products")}
      />
    </FormCard>
  );
};

export default EditFormProduct;
